<template>
  <div class="h-full">
    <UIFloatingActionButton
      v-if="!isPastEvent"
      v-show="FABIsVisible && !guestIsInitialLoading"
      :label-is-visible="FABLabelIsVisible"
      class="bg-secondary z-2000 laptop:hidden"
      @click="onGuestAddPress"
    >
      <template slot="icon">
        <FontAwesomeIcon
          :icon="['far', 'plus']"
          class="fab-icon text-xl text-white"
        />
      </template>
      <span class="text-white">
        Add guest
      </span>
    </UIFloatingActionButton>

    <div class="h-full flex flex-col">
      <div class="sticky top-header p-20 bg-gray-100 border-b border-gray-300 z-2000 overflow-hidden tablet:py-25
      tablet:px-30">
        <div class="flex items-end justify-between mb-15">
          <ElPopover
            placement="bottom-start"
            width="250"
            popper-class="guests-count-overview"
            trigger="hover"
          >
            <span
              slot="reference"
              class="flex-shrink-0 text-primary-light leading-none"
            >
              <span class="inline-block text-4xl touch:text-3xl">
                {{ totalGuestCountLabel }}
              </span>
              <span class="text-lg touch:text-base">
                {{ totalGuestCountLabel === 1 ? 'guest' : 'guests' }}
              </span>
              <FontAwesomeIcon
                :icon="['fas', 'info-circle']"
                class="text-xl text-primary align-top mt-4 touch:text-xl"
              />
            </span>
            <h3 class="font-medium text-primary-light mb-10">Overview</h3>
            <span>{{ guestCountDetails }}</span>
          </ElPopover>
          <div class="flex-shrink-0">
            <button
              v-if="totalGuestCountLabel"
              v-show="visibleButtons.includes('export')"
              :disabled="guestsIsEmpty"
              class="font-semibold text-primary py-8 px-20 border-2 border-primary rounded-8 focus:outline-none
              mobile-m:inline mobile-l:px-25 laptop-l:px-35"
              :class="{
                'mr-10': visibleButtons.length === 2 && !isPastEvent,
                'focus:bg-gray-300 hover:bg-gray-300': !guestsIsEmpty,
                'cursor-not-allowed opacity-75': guestsIsEmpty,
              }"
              @click="onGuestExportPress"
            >
              Export
              <span
                v-if="isPastEvent"
                class="hidden mobile-m:inline"
              >
                guests
              </span>
            </button>
            <button
              v-if="!isPastEvent"
              v-show="visibleButtons.includes('import')"
              class="text-white tracking-wide py-8 px-20 bg-primary border-2 border-primary rounded-8 focus:outline-none
              focus:opacity-75 hover:opacity-75 mobile-l:px-25 laptop:mr-10 laptop-l:px-30"
              @click="onGuestImportPress"
            >
              Import
              <span class="hidden tablet:inline">
                guests
              </span>
            </button>
            <button
              v-if="!isPastEvent"
              class="hidden text-white tracking-wide bg-secondary py-8 px-40 border-2 border-secondary rounded-8
              focus:outline-none focus:opacity-75 hover:opacity-75 laptop:inline laptop-l:px-45"
              @click="onGuestAddPress"
            >
              Add guest
            </button>
          </div>
        </div>
        <GuestSearch
          class="w-full"
          :first-name-first-order="this.firstNameFirstOrder"
          @guest-update="onGuestUpdate($event)"
        />
      </div>

      <GuestList
        :guests="filteredGuests.guests"
        :list-item-height="58"
        class="flex-1 flex flex-col z-10"
        @guest-update="onGuestUpdate($event)"
        @guest-delete="onGuestDelete($event)"
      />
      <GuestModal
        v-model="modal.showing"
        :type="modal.type"
        :loading="modal.loading"
        :event="currentEvent"
        :first-name-first-order="this.firstNameFirstOrder"
        :existing-guest="modalGuestData"
        :badge-support="currentEvent && currentEvent.isSupportingBadge"
        :qr-code-support="currentEvent && currentEvent.isSupportingQRCode"
        :barcode-value-support="currentEvent && !!currentEvent.guestIdFieldForCheckIn"
        :max-additional-guests="maxAdditionalGuests"
        @hidden="modal.loading = false"
        @upgrade-event="onUpgradeEvent"
        @guest-update="onGuestUpdate($event)"
        @guest-delete="onGuestDelete($event)"
        @add-guest="addGuest($event)"
        @update-guest="updateGuest($event)"
        @delete-guest="deleteGuest($event)"
      />
      <GuestExport :event="currentEvent" />
      <GuestImport :event="currentEvent" />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import Compressor from 'compressorjs';
import { v4 as UUID } from 'uuid';
import { FBModule, FBStorageRef } from '@/helpers/firebaseServices';
import { eventGuestStoragePath } from '@/helpers/cloudStorageRefs';
import {
  FSGetUserEvent, FSGetUserTeamMembers, FSGetEventGuests, FSGetEventGuest,
} from '@/helpers/firestoreRefs';
import { showErrorNotification } from '@/utils/notification';
import guestPluralRules from '@/utils/pluralRules/guest';
import extraGuestPluralRules from '@/utils/pluralRules/extraGuest';
import windowSize from '@/mixins/windowSize';
import windowScrollPosition from '@/mixins/windowScrollPosition';
import UIFloatingActionButton from '@/components/UI/UIFloatingActionButton';
import GuestModal from '@/components/guest/GuestModal';
import GuestSearch from '@/components/guest/GuestSearch';
import GuestList from '@/components/guest/list/GuestList';
import GuestExport from '@/components/guest/export/GuestExport';
import GuestImport from '@/components/guest/import/GuestImport';
import guestModule from '@/store/modules/guest';

const mobileSBreakpoint = 320; // TODO: Take from TailwindCSS config
const mobileLargeMinWidth = 425;

function normalizeAddGuestData(guestData) {
  return {
    ...guestData.firstName !== undefined && { firstName: `${guestData.firstName}` },
    ...guestData.lastName !== undefined && { lastName: `${guestData.lastName}` },
    ...guestData.photoStoragePath && { photoStoragePath: `${guestData.photoStoragePath}` },
    ...guestData.email && { email: `${guestData.email}` },
    ...guestData.status !== undefined && { status: parseInt(guestData.status, 10) },
    ...guestData.vip !== undefined && { vip: !!guestData.vip },
    ...guestData.totalExtraGuests !== undefined && { totalExtraGuests: parseInt(guestData.totalExtraGuests, 10) },
    ...guestData.phoneNumber && { phone: `${guestData.phoneNumber}` },
    ...guestData.jobTitle && { jobPosition: `${guestData.jobTitle}` },
    ...guestData.organization && { company: `${guestData.organization}` },
    ...guestData.note && { notes: `${guestData.note}` },
    // eslint-disable-next-line max-len
    ...guestData.attendingTime && typeof guestData.attendingTime.toDate === 'function' && { attendingTime: guestData.attendingTime.toDate() },
    ...guestData.checkInTime && typeof guestData.checkInTime.toDate === 'function' && { checkInTime: guestData.checkInTime.toDate() },
    ...guestData.checkOutTime && typeof guestData.checkOutTime.toDate === 'function' && { checkOutTime: guestData.checkOutTime.toDate() },
    ...guestData.createdBy && { createdBy: `${guestData.createdBy}` },
    ...guestData.barcodeValue !== undefined && guestData.barcodeValue !== '' && { barcodeValue: guestData.barcodeValue },
  };
}

function cleanUpdateGuestData(oldGuestData, guestData) { // Removes fields that don't need updates
  const normalizedObj = {};

  if (guestData?.firstName && oldGuestData?.firstName !== guestData.firstName) {
    normalizedObj.firstName = guestData.firstName;
  }
  if (guestData?.lastName && oldGuestData?.lastName !== guestData.lastName) {
    normalizedObj.lastName = guestData.lastName;
  }
  if (oldGuestData?.photoStoragePath !== guestData.photoStoragePath) {
    normalizedObj.photoStoragePath = guestData.photoStoragePath;
  }
  if (guestData?.email && oldGuestData?.email !== guestData.email) {
    normalizedObj.email = guestData.email;
  }
  if (guestData?.status !== undefined && oldGuestData?.status !== guestData.status) {
    normalizedObj.status = guestData.status;
  }
  if (guestData?.vip !== undefined && oldGuestData?.vip !== guestData.vip) {
    normalizedObj.vip = guestData.vip;
  }
  if (guestData?.totalExtraGuests !== undefined && oldGuestData?.totalExtraGuests !== guestData.totalExtraGuests) {
    normalizedObj.totalExtraGuests = guestData.totalExtraGuests;
  }
  if (guestData?.phoneNumber && oldGuestData?.phoneNumber !== guestData.phoneNumber) {
    normalizedObj.phoneNumber = guestData.phoneNumber;
  }
  if (guestData?.jobTitle && oldGuestData?.jobTitle !== guestData.jobTitle) {
    normalizedObj.jobTitle = guestData.jobTitle;
  }
  if (guestData?.organization && oldGuestData?.organization !== guestData.organization) {
    normalizedObj.organization = guestData.organization;
  }
  if (guestData?.note && oldGuestData?.note !== guestData.note) {
    normalizedObj.note = guestData.note;
  }
  if (guestData?.barcodeValue !== undefined && oldGuestData?.barcodeValue !== guestData.barcodeValue) {
    normalizedObj.barcodeValue = guestData.barcodeValue;
  }

  return normalizedObj;
}

function prepareGuestDataForFB(guestData) {
  const normalizedObj = { ...guestData };

  if (guestData.phoneNumber) {
    normalizedObj.phone = guestData.phoneNumber;
    delete normalizedObj.phoneNumber;
  }
  if (guestData.jobTitle) {
    normalizedObj.jobPosition = guestData.jobTitle;
    delete normalizedObj.jobTitle;
  }
  if (guestData.organization) {
    normalizedObj.company = guestData.organization;
    delete normalizedObj.organization;
  }
  if (guestData.note) {
    normalizedObj.notes = guestData.note;
    delete normalizedObj.note;
  }

  return normalizedObj;
}

export default {
  props: {
    eventID: {
      type: String,
      default: '',
    },
  },
  components: {
    UIFloatingActionButton,
    GuestModal,
    GuestSearch,
    GuestList,
    GuestExport,
    GuestImport,
  },
  mixins: [ // TODO: Replace with Composition API or CSS only button
    windowSize('windowSize', 50, { maxWait: 50 }),
    windowScrollPosition('windowScrollPosition', 20, { leading: true, maxWait: 50 }),
  ],
  beforeRouteLeave(to, from, next) {
    // Intercept route push when modal is open, to close it instead of navigating to the new route
    if (this.modal.showing && !this.noRouteCapture) {
      this.modal.showing = false;
      return;
    }

    next();
  },
  data: () => ({
    unsubUserEvent: null,
    unsubUserTeamMembers: null,
    unsubEventGuestsRef: null,
    replaceExistingTeamMembers: true,
    FABIsVisible: true,
    FABLabelIsVisible: false,
    previousWindowScrollPositionY: 0,
    showGuestDialog: false,
    modal: {
      showing: false,
      loading: false,
      type: 'guest-add', // One modal at any point in time
      guestId: '',
    },
    firstNameFirstOrder: false,
    userEventLoadedInit: false,
    noRouteCapture: false, // Set to true when routing in the modal
    unwatchEventFirstNameFirstOrder: null,
  }),
  created() {
    if (!this.$store.state.guest) this.$store.registerModule('guest', guestModule);

    this.unsubUserEvent = this.subUserEvent(this.eventID);
    this.unsubUserTeamMembers = this.subUserTeamMembers(this.$store.state.user.auth.authData.id);

    // Watch current event
    // eslint-disable-next-line func-names
    this.unwatchEventFirstNameFirstOrder = this.$watch(function () {
      // Watching eventLoadingStatus and event's firstNameFirstOrder as the normal watcher
      // won't trigger (happens in the same tick)
      if (this.eventLoadingStatus !== 'snapshot-loaded') return null; // Return null to trigger watcher

      return this.allEvents[this.eventID]?.firstNameFirstOrder;
      // eslint-disable-next-line func-names
    }, function (value) {
      if (this.eventLoadingStatus !== 'snapshot-loaded') return;

      const firstNameFirstOrder = Boolean(value);

      if (firstNameFirstOrder === this.firstNameFirstOrder && Boolean(this.unsubEventGuestsRef)) return;
      this.firstNameFirstOrder = firstNameFirstOrder;

      // Unsubscribe previous snapshot listener
      if (this.unsubEventGuestsRef !== null) this.unsubEventGuestsRef();

      // Resetting guests and filtered guests state
      this.$store.dispatch('guest/resetGuestState');
      this.$store.dispatch('guest/filter/resetGuestFilterState');

      this.unsubEventGuestsRef = FSGetEventGuests(this.eventID)
        .orderBy(value ? 'firstName' : 'lastName', 'asc')
        .orderBy(value ? 'lastName' : 'firstName', 'asc')
        .onSnapshot(async (snapshot) => {
          await this.onGuestsSnapshotChange({
            snapshot,
            firstNameFirstOrder,
          });
          this.updateGuestIsInitialLoading(false);
        }, (error) => {
          this.updateGuestIsInitialLoading(false);
          console.error(error);
        });
    }, { immediate: true });
  },
  watch: {
    'currentEvent.title': {
      immediate: true,
      handler(value) {
        this.$store.commit('navigation/SET_CURRENT_NAVIGATION_TITLE_BASE', value || '');
      },
    },
    'windowSize.width': {
      immediate: true,
      handler(value) {
        this.FABLabelIsVisible = value > mobileLargeMinWidth;
      },
    },
    'windowScrollPosition.y': {
      immediate: true,
      handler(value) {
        if (this.windowSize.width > mobileLargeMinWidth) {
          this.FABIsVisible = true;
          this.FABLabelIsVisible = value <= this.previousWindowScrollPositionY;
          this.previousWindowScrollPositionY = value;

          return;
        }

        this.FABIsVisible = value <= this.previousWindowScrollPositionY || this.previousWindowScrollPositionY <= 0;
        this.previousWindowScrollPositionY = value;
      },
    },
    maxAdditionalGuests: {
      handler(value) {
        if (value === 0 && this.modal.showing && this.modal.type === 'guest-add') {
          this.modal.type = 'guest-add-limit-reached';
        }
      },
    },
  },
  computed: {
    ...mapState('user/events', {
      eventLoadingStatus: 'loadingStatus',
      allEvents: 'all',
    }),
    ...mapGetters('user/auth', {
      userId: 'authDataId',
    }),
    ...mapGetters('guest', {
      guestsIsEmpty: 'guestsIsEmpty',
    }),
    ...mapGetters('guest/filter', {
      filteredGuests: 'filteredGuests',
    }),
    guestIsInitialLoading() {
      return this.$store.state?.guest?.isInitialLoading ?? true;
    },
    currentEvent() {
      if (!this.eventID || !this.allEvents[this.eventID]) return null;
      return this.allEvents[this.eventID];
    },
    maxAdditionalGuests() {
      if (this.currentEvent?.maxGuests && this.$store.state?.guest?.guestsCounts) {
        return Math.max(this.currentEvent.maxGuests
          - (this.$store.state.guest.guestsCounts.mainGuest + this.$store.state.guest.guestsCounts.extraGuest), 0);
      }
      return 0;
    },
    totalGuestCountLabel() {
      return this.filteredGuests.guestsCounts.mainGuest + this.filteredGuests.guestsCounts.extraGuest;
    },
    guestCountDetails() {
      return `${guestPluralRules(this.filteredGuests.guestsCounts.mainGuest)}
        ${this.filteredGuests.guestsCounts.extraGuest ? `(+${extraGuestPluralRules(this.filteredGuests.guestsCounts.extraGuest)})` : ''}`;
    },
    visibleButtons() {
      if (this.windowSize.width <= mobileSBreakpoint) {
        if (this.currentEvent?.status === 1) return ['export'];
        return ['import'];
      }
      return ['export', 'import'];
    },
    isPastEvent() {
      return !this.currentEvent || this.currentEvent?.status === 1;
    },
    modalGuestData() {
      if (!this.$store.state?.guest?.guests) return undefined;
      return this.$store.state.guest.guests.find((guest) => guest.id === this.modal.guestId);
    },
  },
  methods: {
    ...mapActions('navigation', {
      updateCurrentNavigationTitleBase: 'updateCurrentNavigationTitleBase',
    }),
    ...mapActions('user/teamMembers', {
      onTeamMembersSnapshot: 'onSnapshot',
    }),
    ...mapActions('guest', {
      updateGuestIsInitialLoading: 'updateIsInitialLoading',
      onGuestsSnapshotChange: 'onGuestsSnapshotChange',
    }),
    ...mapActions('guest/export', {
      updateGuestExportDialogIsVisible: 'updateDialogIsVisible',
    }),
    ...mapActions('guest/import', {
      updateGuestImportDialogIsVisible: 'updateDialogIsVisible',
    }),
    ...mapActions('user/events', {
      onUserEventSnapshot: 'onEventSnapshot',
    }),
    subUserEvent(eventId) {
      return FSGetUserEvent(eventId)
        .onSnapshot((eventSnapshot) => {
          this.onUserEventSnapshot(eventSnapshot);
        }, (error) => {
          console.error(error);
        });
    },
    subUserTeamMembers(userId) {
      return FSGetUserTeamMembers(userId)
        .orderBy('name')
        .onSnapshot((snapshot) => {
          this.onTeamMembersSnapshot({
            snapshot,
            replaceExistingState: this.replaceExistingTeamMembers,
          });

          // Don't replace the team members on subsequent snapshots
          this.replaceExistingTeamMembers = false;
        }, (error) => {
          console.error(error);
        });
    },
    onGuestExportPress() {
      this.updateGuestExportDialogIsVisible(true);
    },
    onGuestImportPress() {
      this.updateGuestImportDialogIsVisible(true);
    },
    onUpgradeEvent() {
      this.noRouteCapture = true;
      this.$router.push({ name: 'EventUpgrade', params: { eventID: this.eventID } });
    },
    onGuestAddPress() {
      this.modal.showing = true;
      this.modal.guestId = '';

      if (this.maxAdditionalGuests) {
        // Show add guest modal
        this.modal.type = 'guest-add';
        return;
      }

      // Show max guests limit modal
      this.modal.type = 'guest-add-limit-reached';
    },
    onGuestUpdate(guestId) {
      this.modal.type = 'guest-update';
      this.modal.showing = true;
      this.modal.guestId = guestId;
    },
    onGuestDelete(guestId) {
      this.modal.type = 'guest-delete';
      this.modal.showing = true;
      this.modal.guestId = guestId;
    },
    compressPhoto(photoFile) {
      return new Promise((resolve, reject) => {
        // eslint-disable-next-line no-new
        new Compressor(photoFile, {
          quality: 0.95,
          maxWidth: 512,
          success: resolve,
          error: reject,
        });
      });
    },
    async addGuest({ guest, photoFile }) {
      this.modal.loading = true;

      // Uploading photo first, then adding the guest document
      try {
        // Upload photo if available
        let photoStoragePath = '';
        if (photoFile) {
          let compressedPhotoFile = null;

          photoStoragePath = eventGuestStoragePath(this.eventID, `${UUID()}.jpeg`);

          try {
            compressedPhotoFile = await this.compressPhoto(photoFile);
          } catch (error) {
            console.error(error);
          } finally {
            await FBStorageRef.child(photoStoragePath).put(compressedPhotoFile || photoFile);
          }
        }

        const nowTimestamp = FBModule.firestore.Timestamp.fromDate(new Date());
        const docData = {
          ...normalizeAddGuestData(guest),
          ...photoStoragePath && { photoStoragePath },
          // Additional metadata
          ...guest.status === 2 && { checkInType: 3 },
          creationTime: nowTimestamp,
          modificationTime: nowTimestamp,
          createdBy: this.userId,
          source: 3,
          ...guest.status === 1 && { attendingTime: nowTimestamp },
          ...guest.status === 2 && { checkInTime: nowTimestamp },
          ...guest.status === 3 && { checkOutTime: nowTimestamp },
        };

        await FSGetEventGuests(this.eventID).add(docData);

        this.modal.showing = false;
      } catch (error) {
        this.modal.loading = false;
        showErrorNotification(error.message);
        console.error(error);
      }
    },
    async updateGuest({ oldGuestData, guestData, photo }) {
      this.modal.loading = true;

      // Uploading photo first, then adding the guest document
      try {
        let photoStoragePath = oldGuestData.photoStoragePath || '';

        // Delete old photo
        if (oldGuestData.photoStoragePath && photo?.edited) {
          await FBStorageRef.child(oldGuestData.photoStoragePath).delete();
          photoStoragePath = '';
        }

        // Upload photo if available
        if (photo?.file) {
          let compressedPhotoFile = null;
          photoStoragePath = eventGuestStoragePath(this.eventID, `${UUID()}.jpeg`);

          photoStoragePath = eventGuestStoragePath(this.eventID, `${UUID()}.jpeg`);

          try {
            compressedPhotoFile = await this.compressPhoto(photo.file);
          } catch (error) {
            console.error(error);
          } finally {
            await FBStorageRef.child(photoStoragePath).put(compressedPhotoFile || photo.file);
          }
        }

        const nowTimestamp = FBModule.firestore.Timestamp.fromDate(new Date());
        const docData = {
          ...prepareGuestDataForFB(cleanUpdateGuestData(oldGuestData, {
            ...guestData,
            photoStoragePath,
          })),
          // Additional metadata
          ...guestData.status === 2 && { checkInType: 3 },
          modificationTime: nowTimestamp,
        };

        // Tracking status change times
        if (guestData.status === 0) { // Status: No-status
          if (guestData.attendingTime) docData.attendingTime = FBModule.firestore.FieldValue.delete();
          if (guestData.checkInTime) docData.checkInTime = FBModule.firestore.FieldValue.delete();
          if (guestData.checkOutTime) docData.checkOutTime = FBModule.firestore.FieldValue.delete();
        } else if (guestData.status === 1) { // Status: Attending
          if (!guestData.attendingTime) docData.attendingTime = nowTimestamp;
          if (guestData.checkInTime) docData.checkInTime = FBModule.firestore.FieldValue.delete();
          if (guestData.checkOutTime) docData.checkOutTime = FBModule.firestore.FieldValue.delete();
        } else if (guestData.status === 2) { // Status: Checked-in
          if (!guestData.checkInTime) docData.checkInTime = nowTimestamp;
          if (guestData.checkOutTime) docData.checkOutTime = FBModule.firestore.FieldValue.delete();
        } else if (guestData.status === 3) { // Status: Checked-out
          if (!guestData.checkOutTime) docData.checkOutTime = nowTimestamp;
        }

        await FSGetEventGuest(this.eventID, oldGuestData.id).update(docData);

        this.modal.showing = false;
      } catch (error) {
        this.modal.loading = false;
        showErrorNotification(error.message);
        console.error(error);
      }
    },
    async deleteGuest({ guestId, photoStoragePath }) {
      try {
        const deletionPromises = [];

        if (photoStoragePath) deletionPromises.push(FBStorageRef.child(photoStoragePath).delete());
        deletionPromises.push(FSGetEventGuest(this.eventID, guestId).delete());

        await Promise.all(deletionPromises);

        this.modal.showing = false;
      } catch (error) {
        showErrorNotification(error.message);
        console.error(error);
      }
    },
  },
  beforeDestroy() {
    // Unsubscribe all unnecessary realtime listeners
    if (this.unsubUserEvent !== null) this.unsubUserEvent();
    if (this.unsubUserTeamMembers !== null) this.unsubUserTeamMembers();
    if (this.unsubEventGuestsRef !== null) this.unsubEventGuestsRef();
    if (this.unwatchEventFirstNameFirstOrder !== null) this.unwatchEventFirstNameFirstOrder();

    // Unregister VueX modules that aren't necessary anymore
    // Resets the state of guests, guest filters, selected filters, export and import
    if (this.$store.state.guest) this.$store.unregisterModule('guest');

    this.updateCurrentNavigationTitleBase('');
  },
};
</script>

<style scoped>
.fab-icon {
  @apply w-30 !important;
  @apply h-30;
}

.el-button + .el-button {
  @apply ml-0;
}
</style>
